/**
 * Turning what somebody typed into something the switch can dial.
 *
 * A number reaches this console from a lead record, a spreadsheet cell, a CRM field or somebody
 * reading it aloud, and it arrives punctuated: `+1 718-998-4009`, `(718) 998-4009`,
 * `+1 718–998-4009` with an en dash because a word processor helpfully replaced the hyphen. A
 * SIP destination has no room for any of that — the dial string is space-delimited, and the
 * dialplan matches `^\+?1?([2-9]\d{2}[2-9]\d{6})$`, which a space defeats.
 *
 * So one function, applied where the console hands a destination to the switch, and nowhere
 * else. It is deliberately NOT applied as the rep types: rewriting a field under a caret moves
 * the caret, and a rep correcting a digit in the middle of a number would find the cursor
 * somewhere else on every keystroke.
 *
 * **It does not add a country code.** `+1` for a bare ten-digit number is the dialplan's job —
 * `pstn_extension` bridges `+1$1` — and a second copy of that rule in the console is a second
 * thing to change when this platform dials outside the NANP. Stripping punctuation is the whole
 * job here; deciding what a number MEANS belongs to the switch.
 */

/**
 * Every character that can appear between the digits of a number a human wrote.
 *
 * The unicode ones are the reason this is a named set rather than a `/[-\s()]/` in the middle of
 * a function. A number copied out of a document carries an en dash (U+2013) or a non-breaking
 * hyphen (U+2011) where the writer typed a hyphen, and a number copied out of a spreadsheet
 * carries a non-breaking space (U+00A0). None of them are what they look like, all of them break
 * the dial string, and none of them are visible in a diff.
 */
const SEPARATORS = /[\s()\.\u002D\u2010\u2011\u2012\u2013\u2014\u2015\u2212]/g;
//                   |   |  |   |       |_______________________________|        |
//                   |   |  |   hyphen-minus, the one on the keyboard    |        minus sign
//                   |   |  the dot in 718.998.4009                      the dashes a word
//                   |   parentheses around an area code                 processor substitutes
//                   every space JS calls whitespace, which includes the non-breaking one a
//                   spreadsheet pastes and the narrow one some locales use as a group separator
//
// Written as escapes rather than as the characters themselves for two reasons: a literal `-`
// inside a class is a RANGE unless it is at an edge — the first version of this read
// `[\s   -   ().-–—]` and quietly ate every digit between `)` and `-` — and none of the unicode
// dashes are distinguishable from a hyphen in a diff, a code review, or an editor.

/** A destination made only of the things a dial string may carry: digits, `+`, `*`, `#`. */
const DIALABLE = /^\+?[0-9*#]+$/;

/**
 * The destination to send, or the input trimmed when it is not a number at all.
 *
 * Separators are removed only when what is left is something the switch could dial. Anything
 * else comes back trimmed and otherwise untouched, because it is not a punctuated number — it is
 * a SIP URI, a conference name, a room, or a rep typing a colleague's name into the wrong box —
 * and squeezing the spaces out of those turns a recognisable mistake into an unrecognisable one.
 *
 *     "+1 718–998-4009"  → "+17189984009"
 *     "(718) 998-4009"   → "7189984009"     the dialplan adds the +1
 *     "1004"             → "1004"           an extension is already dialable
 *     "*97"              → "*97"
 *     "1001-room"        → "1001-room"      a room name, and the hyphen is part of it
 *     "sip:a@b.example"  → "sip:a@b.example"
 */
export function normalizeDialString(raw: string): string {
  const trimmed = raw.trim();
  const stripped = trimmed.replace(SEPARATORS, "");
  return DIALABLE.test(stripped) ? stripped : trimmed;
}

/**
 * Whether a number off a *record* is something worth offering to dial.
 *
 * Not everything in a CDR's number column is a number. A caller who withheld their id arrives as
 * `unknown`, `anonymous` or `restricted`; a leg that went into a conference names a room
 * (`1001-room`); FreeSWITCH's own placeholder destination is `s`. All of those are printable,
 * none of them are callable, and a Call button on one is a promise the switch cannot keep — the
 * rep confirms a call and gets a hangup cause instead.
 *
 * The test is applied to the **dial string**, not to the input: `(310) 555-0142` passes because
 * `normalizeDialString` gets the punctuation out of it, and `1001-room` fails because what comes
 * back from it still has letters in — the hyphen there is part of a name, not punctuation. One
 * character is switch noise rather than a number anybody meant, so two is the floor.
 *
 * Deliberately permissive above that: it does not check length, area code or country. Offering
 * to dial something the switch then refuses costs one hangup cause; refusing to offer a number
 * that would have connected is a feature that does not work, and the dialplan — not the console
 * — is where what a destination MEANS is decided.
 */
export function isDialable(raw: string | null | undefined): boolean {
  if (!raw) return false;
  const dest = normalizeDialString(raw);
  return dest.length >= 2 && DIALABLE.test(dest);
}

/**
 * A US or Canadian number, however it was typed — the one kind of destination a campaign will
 * dial for the rep (ADR-047 in the platform repo).
 *
 * The same rule as the dialplan's `PSTN_EXPRESSION` and core-api's `manual_dial_number`, on the
 * dial string: ten significant digits, neither the area code nor the exchange starting with 0
 * or 1, with or without a leading 1 or +1. A four-digit extension never matches, and neither
 * does an international number, a short code or a room — those keep going out of the rep's own
 * phone, straight to the switch. The console decides the path with this and core-api refuses
 * anything else, so the two cannot disagree about what "an outside number" means.
 */
const NANP = /^\+?1?[2-9]\d{2}[2-9]\d{6}$/;

export function isPstnNumber(raw: string | null | undefined): boolean {
  if (!raw) return false;
  return NANP.test(normalizeDialString(raw));
}

/** E.164: a `+`, a country code that does not start with 0, fifteen digits at most in all. */
const E164 = /^\+[1-9]\d{6,14}$/;

/**
 * A number a caller from outside can call from: a US or Canadian number however it was typed, or
 * any number written with its country code, punctuation and all. Not an extension, a short code,
 * a room, or the `anonymous` of a caller who hides theirs.
 *
 * The same rule as core-api's `normalize_e164`, which refuses everything else, so the phone, the
 * missed calls, the block dialog and the server agree on what a caller's number is.
 */
export function isOutsideNumber(raw: string | null | undefined): raw is string {
  if (!raw) return false;
  return isPstnNumber(raw) || E164.test(normalizeDialString(raw));
}

/**
 * An outside number as core-api keeps a caller's: E.164, a US or Canadian number with its +1.
 * Null for anything that is not an outside number. This is not a dial string (see the top of this
 * file): it is the one spelling two copies of a number are compared and stored in.
 *
 *     "(310) 555-0142"    → "+13105550142"
 *     "+44 20 7946 0958"  → "+442079460958"
 *     "1004"              → null
 */
export function toE164(raw: string | null | undefined): string | null {
  if (!isOutsideNumber(raw)) return null;
  const dest = normalizeDialString(raw);
  return isPstnNumber(dest) ? `+1${dest.replace(/\D/g, "").slice(-10)}` : dest;
}

/** Said under a phone field that holds anything but a US number (ACL-653). */
export const US_PHONE_PROBLEM = "Enter a US number: ten digits, like (310) 555 0142.";

/**
 * A number typed into a contact's phone or a phone field as it is stored (ACL-653): +1 and ten digits,
 * with or without the +1, punctuation and all. Null for anything else: every number we work with is a
 * US one, so a rep never has to think about a country code. Imports and webhooks go by `normalize_e164`
 * on the server, which still takes any country.
 *
 *     "3105550177"        → "+13105550177"
 *     "1 (310) 555-0177"  → "+13105550177"
 *     "+44 20 7946 0958"  → null
 */
export function usPhone(raw: string | null | undefined): string | null {
  return isPstnNumber(raw) ? toE164(raw) : null;
}

/** How many digits a US number has after its +1. */
export const US_DIGITS = 10;

/**
 * What a number shows after a fixed +1 (`components/ui/phone-input.tsx`): its digits, without the
 * 1 of a country code written in front of them. No US area code starts with 1, so a leading 1 is
 * only ever the country code, with or without its +. A number from another country kept from
 * before ACL-653 keeps every digit, so a supervisor who leaves it alone does not change it.
 *
 *     "+1 310 555 0142"  → "3105550142"
 *     "(310) 555-0142"   → "3105550142"
 *     "+442079460958"    → "442079460958"
 */
export function usDigits(raw: string | null | undefined): string {
  if (!raw) return "";
  return raw.replace(/\D/g, "").replace(/^1/, "");
}

/** What the field keeps of a keystroke or a paste: `usDigits`, ten at most. */
export function typedUsDigits(typed: string): string {
  return usDigits(typed).slice(0, US_DIGITS);
}

/**
 * The digits after the +1 as the field draws them while they are typed, the way the console
 * writes a number (`formatPhoneNumber`): `(310`, `(310) 555`, `(310) 555 0142`. The bracket
 * closes and a space comes only once a digit follows, so a backspace always takes a digit and
 * never only the punctuation the field would put straight back. More than ten digits (a number
 * from another country) are drawn as they are.
 */
export function formatUsDigits(digits: string): string {
  if (digits.length > US_DIGITS) return digits;
  const area = digits.slice(0, 3);
  const exchange = digits.slice(3, 6);
  const line = digits.slice(6);
  if (digits.length <= 3) return area ? `(${area}` : "";
  if (digits.length <= 6) return `(${area}) ${exchange}`;
  return `(${area}) ${exchange} ${line}`;
}

/** Where the caret goes in `drawn` to sit right after its `n`th digit: the field redraws what was
 *  typed and the caret must not jump to the end of it. */
export function caretAfterDigits(drawn: string, n: number): number {
  if (n <= 0) return 0;
  let seen = 0;
  for (let i = 0; i < drawn.length; i += 1) {
    if (/\d/.test(drawn.charAt(i))) {
      seen += 1;
      if (seen === n) return i + 1;
    }
  }
  return drawn.length;
}

/**
 * Whether two numbers are the same line, however each was spelled: the switch presents a
 * caller as `+12135559001`, a lead is stored the same way, and an extension or a short code is
 * compared whole. Ten-digit NANP numbers compare on their last ten digits, so a missing `+1`
 * on one side does not make two spellings of one number look like two numbers.
 */
export function sameNumber(a: string | null | undefined, b: string | null | undefined): boolean {
  if (!a || !b) return false;
  const da = a.replace(/\D/g, "");
  const db = b.replace(/\D/g, "");
  if (!da || !db) return false;
  if (da.length >= 10 && db.length >= 10) return da.slice(-10) === db.slice(-10);
  return da === db;
}
